<template>
  <v-page>
    <toolbar fixed>
      <template v-slot:left>
        <span class="v-toolbar__text">{{
          $t("pages.account.detail.accountDetails")
        }}</span>
      </template>
    </toolbar>

    <list
      class="mt-3"
      boxed
      :heading="$t('pages.account.detail.yourDetailHeading')"
    >
      <list-item
        @click="() => editAttribute(ATTRIBUTES.NAME)"
        data-cy="details-name"
      >
        <template slot="left">
          <v-icon icon="person" />
        </template>
        <div class="--centered ml-1">{{ $t("pages.account.detail.name") }}</div>

        <template slot="right">
          {{ currentUser.name }}
        </template>
      </list-item>

      <list-item
        @click="() => editAttribute(ATTRIBUTES.EMAIL)"
        data-cy="details-email"
      >
        <template slot="left">
          <v-icon icon="mail" />
        </template>
        <div class="--centered ml-1">
          {{ $t("pages.account.detail.email") }}
        </div>

        <template slot="right">
          {{ currentUser.email }}
        </template>
      </list-item>

      <list-item
        data-cy="details-language"
        @click="() => editAttribute(ATTRIBUTES.LANGUAGE)"
      >
        <template slot="left">
          <v-icon icon="language" />
        </template>
        <div class="--centered ml-1">
          {{ $t("pages.account.detail.language") }}
        </div>

        <template slot="right">
          {{ languageLabel }}
        </template>
      </list-item>

      <list-item
        @click="() => editAttribute(ATTRIBUTES.TIMEZONE)"
        data-cy="details-timezone"
      >
        <template slot="left">
          <v-icon icon="schedule" />
        </template>
        <div class="--centered ml-1">
          {{ $t("pages.account.detail.timezone") }}
        </div>

        <template slot="right">
          {{ timezoneText }}
        </template>
      </list-item>

      <list-item
        v-for="property in allPropertiesList"
        align-center
        :key="property.slug"
        :disabled="isPropertyDeleted(property.slug)"
        data-cy="details-property"
        @click="() => handlePropertyClick(property)"
      >
        <template slot="left">
          <v-icon :icon="FIELD_ICONS[property.field_type]" />
        </template>
        <div class="--centered ml-1">{{ property.name }}</div>

        <template slot="right">
          <property-value
            :value="subscriberSubmittedProperties[property.slug]"
            :type="property.field_type"
            class="details-submitted-properties"
          />
        </template>
      </list-item>
    </list>

    <v-modal v-if="editingAttribute" short @close="closeModal">
      <template :slot="DETAILS_MODAL">
        <form class="px-2 py-3" @submit.prevent="updateAccountDetails">
          <template v-if="editingAttribute === ATTRIBUTES.NAME">
            <label class="mb-2 d-block text-large font-weight-semi-bold">
              {{ $t("pages.account.detail.name") }}
            </label>

            <v-input
              class="mb-2"
              type="name"
              v-model="vName"
              name="name"
              :placeholder="$t('pages.account.detail.yourNamePlaceholder')"
              required
            />

            <cta-row white-bg overlay-modal row>
              <v-button
                primary
                block
                type="submit"
                :is-loading="isLoading"
                @click="updateAccountDetails"
              >
                {{ $t("pages.account.detail.updateName") }}
              </v-button>
            </cta-row>
          </template>

          <template v-if="editingAttribute === ATTRIBUTES.EMAIL">
            <label class="mb-2 d-block text-large font-weight-semi-bold">
              {{ $t("pages.account.detail.email") }}
            </label>

            <v-input
              class="mb-2"
              type="email"
              v-model="vEmail"
              name="email"
              :placeholder="$t('pages.account.detail.yourEmailPlaceholder')"
              required
            />

            <cta-row white-bg overlay-modal row>
              <v-button
                primary
                block
                type="submit"
                :is-loading="isLoading"
                @click="updateAccountDetails"
              >
                {{ $t("pages.account.detail.updateEmail") }}
              </v-button>
            </cta-row>
          </template>

          <template v-if="editingAttribute === ATTRIBUTES.LANGUAGE">
            <label class="mb-2 d-block text-large font-weight-semi-bold">
              {{ $t("pages.account.detail.labelSelectLanguage") }}
            </label>
            <v-radio
              v-for="(option, k) in optionLanguages"
              :key="k"
              :current-value="vLanguage"
              :value="String(option.value)"
              @change="value => handleSelectLanguage({ value })"
            >
              <label>{{ option.label }}</label>
            </v-radio>
            <cta-row white-bg overlay-modal row>
              <v-button
                primary
                block
                type="submit"
                :is-loading="isLoading"
                @click="updateLanguageDetails"
              >
                {{ $t("pages.account.detail.updateLanguage") }}
              </v-button>
            </cta-row>
          </template>

          <template v-if="editingAttribute === ATTRIBUTES.TIMEZONE">
            <label class="mb-2 d-block text-large font-weight-semi-bold">
              {{ $t("pages.account.detail.timezone") }}
            </label>

            <v-select
              :value="vTimezone"
              label="text"
              item-key="value"
              :options="TIMEZONES"
              :clearable="false"
              @input="timezone => (vTimezone = timezone)"
            />

            <cta-row white-bg overlay-modal row>
              <v-button
                primary
                block
                type="submit"
                :is-loading="isLoading"
                @click="updateAccountDetails"
              >
                {{ $t("pages.account.detail.updateTimezone") }}
              </v-button>
            </cta-row>
          </template>
        </form>
      </template>
    </v-modal>

    <edit-subscriber-property-modal
      v-if="editingProperty"
      :editing-property="editingProperty"
      @close="closePropertyModal"
    />
  </v-page>
</template>

<script>
import vSelect from "vue-select"
import { mapActions, mapGetters } from "vuex"

import TIMEZONES from "@shared/timezones.json"
import supportedLanguages from "@shared/supported-languages"

import i18n from "@/store/i18n"

import { addToHash, removeFromHash } from "@/lib/url-helpers"
import { FIELD_TYPES, FIELD_ICONS } from "@/lib/form-utils"

import vInput from "@/components/forms/vInput"
import vRadio from "@/components/forms/vRadio.vue"
import Toolbar from "@/components/Toolbar"
import List from "@/components/List"
import ListItem from "@/components/ListItem"
import CtaRow from "@/components/CtaRow"
import EditSubscriberPropertyModal, {
  PROPERTY_MODAL
} from "@/components/modals/EditSubscriberPropertyModal"

const DETAILS_MODAL = "#edit-details"

const ATTRIBUTES = {
  NAME: "name",
  EMAIL: "email",
  LANGUAGE: "language",
  TIMEZONE: "timezone"
}

const optionLanguages = supportedLanguages.map(language => ({
  value: language.locale,
  label: language.name
}))

export default {
  components: {
    List,
    ListItem,
    CtaRow,
    vSelect,
    vInput,
    EditSubscriberPropertyModal,
    Toolbar,
    vRadio,
    PropertyValue: () => import("@/components/subscriber-properties/Value")
  },

  data() {
    return {
      editingAttribute: null,
      editingProperty: null,
      vEmail: "",
      vName: "",
      vLanguage: "",
      vTimezone: null,
      isLoading: false,

      loadingProperties: false,

      DETAILS_MODAL,
      PROPERTY_MODAL,
      ATTRIBUTES,
      optionLanguages,
      TIMEZONES,
      FIELD_TYPES,
      FIELD_ICONS
    }
  },

  activated() {
    this.fetchSubscriberProperties()
  },

  methods: {
    ...mapActions("auth", ["update", "updateSubscriber"]),
    ...mapActions("subscriberProperties", [
      "fetchSubscriberProperties",
      "updateSubscriberLanguage"
    ]),

    refreshDetails() {
      this.vEmail = this.currentUser.email
      this.vName = this.currentUser.name
      this.vTimezone =
        TIMEZONES.find(tz => tz.value == this.currentUser.timezone) || null
    },

    updateAccountDetails() {
      this.isLoading = true

      this.update({
        email: this.vEmail,
        name: this.vName,
        timezone: this.vTimezone ? this.vTimezone.value : null
      })
        .then(() => {
          this.$notify(this.$t("pages.account.detail.updateSuccess"))

          this.closeModal()
        })
        .catch(err => {
          this.$notify(
            err?.message || this.$t("pages.account.detail.updateFailed")
          )
        })
        .finally(() => {
          this.refreshDetails()
          this.isLoading = false
        })
    },

    editAttribute(attribute) {
      this.editingAttribute = attribute
      this.$router.push(addToHash(this.$route.hash, DETAILS_MODAL))
    },

    closeModal() {
      this.editingAttribute = null
      // remove from hash
      this.$router.push(removeFromHash(this.$route.hash, DETAILS_MODAL))

      this.vEmail = this.currentUser.email
      this.vName = this.currentUser.name
      this.vLanguage = this.currentUser.language
      this.refreshDetails()
      this.isLoading = false
    },

    editProperty(property) {
      this.editingProperty = property
    },

    handlePropertyClick(property) {
      this.$router.push(addToHash(this.$route.hash, PROPERTY_MODAL))

      this.editProperty(property)
    },

    closePropertyModal() {
      this.editingProperty = null
    },

    isPropertyDeleted(slug) {
      return !this.getSubscriberPropertyBySlug(slug)
    },

    handleSelectLanguage(value) {
      this.vLanguage = value.value
    },

    updateLanguageDetails() {
      this.isLoading = true

      this.updateSubscriber({ language: this.vLanguage })
        .then(() => {
          this.$forceUpdate()
          this.$notify(this.$t("pages.account.detail.updateSuccess"))
          this.closeModal()
        })
        .catch(err => {
          this.$notify(
            err?.message || this.$t("pages.account.detail.updateFailed")
          )
        })
        .finally(() => {
          this.vLanguage = this.currentUser.language
          this.isLoading = false
        })
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),
    ...mapGetters("subscriberProperties", [
      "getSubscriberPropertyBySlug",
      "allPropertiesList",
      "subscriberSubmittedProperties"
    ]),

    languageLabel() {
      const foundItem = this.optionLanguages.find(
        item => item.value === this.vLanguage
      )

      return foundItem
        ? foundItem.label
        : this.optionLanguages.find(l => l.value === i18n.locale)?.label
    },

    timezoneText() {
      return this.vTimezone && this.vTimezone.text
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler(isAuthenticated) {
        if (isAuthenticated) {
          this.vName = this.currentUser.name
          this.vEmail = this.currentUser.email
          this.vLanguage = this.currentUser.language
          this.refreshDetails()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~vue-select/dist/vue-select.css";
@import "@/assets/styles/mixins";

.details-submitted-properties {
  @include overflow-ellipsis;

  max-width: min(50vw, 200px);
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.vs__dropdown-toggle {
  min-height: $input--base-height;

  .vs__selected {
    padding: 0.5rem;
  }
}

.vs__dropdown-option {
  margin-bottom: 0;
  padding-bottom: 0.5rem;
}

.details-submitted-properties {
  img {
    max-width: 80px;
  }
}
</style>
